(ns practitest.frontend.components.attachments3
  (:require [ajax.core :as ajax]
            [clojure.string :as string]
            [practitest.frontend.common-entities.metadata :refer [entities-metadata]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [format-translation get-translation]]
            [practitest.frontend.common.re-frame :refer [conditional-dispatch]]
            [practitest.frontend.common.uri :refer [set-query-param]]
            [practitest.frontend.components.confirmation :as confirmation]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.search-box :as search-box]
            [practitest.frontend.events :as events]
            [practitest.frontend.forms3.entity-form.utils :refer [track!]]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]))

;;; subscriptions

(defn process-backend-attachment-data [data]
  (merge data
         {:size (:file_size data)
          :created-at (:created_at data)
          :type (:file_type data)
          :src (:url data)}))

(re-frame/reg-sub
  ::attachments
  (fn [[_ entity-type]]
    (re-frame/subscribe [::subs/entity entity-type]))
  (fn [{:keys [attachments]} _]
    (mapv process-backend-attachment-data (:data attachments))))

(re-frame/reg-sub
  ::stage-attachments
  (fn [[_ entity-type]]
    (re-frame/subscribe [::subs/entity entity-type]))
  (fn [{:keys [attachments]} _]
    (:stage attachments)))

(re-frame/reg-sub
  ::notifications
  (fn [[_ entity-type]]
    (re-frame/subscribe [::subs/entity entity-type]))
  (fn [{:keys [attachments]} _]
    (:notifications attachments)))

(re-frame/reg-sub
  ::unsaved-attachments?
  (fn [[_ entity-type]]
    (re-frame/subscribe [::subs/entity entity-type]))
  (fn [{:keys [attachments]} _]
    ;; can be in-progress attachments.
    ;; staged attachments that were uploaded to the staging area (for new entity)
    ;; are saved after the create (seq (:stage attachments))
    (seq (:requests attachments))))

;; events
;; NOTE: we currently uploading each attachment separately because we want to have progress for individual attachments
;;

(re-frame/reg-event-db
  ::track-upload
  (fn [db [_ entity-type upload-id request]]
    (update-in db [:entity entity-type :attachments :requests] assoc upload-id request)))

(re-frame/reg-event-fx
 ::failed-upload
 (fn [{db :db} [_ entity-type upload-id _file upload-options result-or-error]]
   (-> {:db (update-in db [:entity entity-type :attachments]
                       (fn [attachments]
                         (cond-> attachments
                           :always (update-in [:notifications :failed] (fnil inc 0))
                           upload-id (update-in [:notifications :uploading] (fnil dec 0))
                           upload-id (update :requests dissoc upload-id)
                           (nil? upload-id) (update-in [:notifications :errors] conj result-or-error))))}
       (conditional-dispatch (:uploaded-attachment-failed-event upload-options) upload-options))))

(re-frame/reg-event-fx
  ::cancel-uploads
  (fn [{:keys [db]} [_ entity-type]]
    (doseq [req (vals (get-in db [:entity entity-type :attachments :requests]))]
      (.abort req))
    {:db (update-in db [:entity entity-type :attachments]
                    (fn [attachments]
                      (-> attachments
                          (assoc-in [:notifications :uploading] 0)
                          (assoc :requests nil))))}))

(defn entity-type->backend
  [entity-type]
  (get-in entities-metadata [entity-type :db-entity]))

;; upload
(defn upload-attachment [{db :db}
                         [_ file {:keys [entity-type entity-id] :as upload-options}]]
  (let [upload-id (random-uuid)]
    {:db (update-in db [:entity entity-type :attachments :notifications :uploading]
                    (fnil inc 0))
     :http-xhrio
     {:method :post
      :uri (broutes/get-url "pattachments#multiupload"
                            {:project_id (subs/get-current-project-id db)})
      :body   (doto (js/FormData.)
                (.append "attachable_id" entity-id)
                (.append "attachable_type" (entity-type->backend entity-type))
                (.append "pattachments[]" file (.-name file)))
      :progress-handler (fn [event]
                          (js/console.log "upload-attachment:progress" event))
      :response-format (ajax/json-response-format {:keywords? true})
      :on-request [::track-upload entity-type upload-id]
      :on-success [::uploaded file upload-id upload-options]
      :on-failure [::failed-upload entity-type upload-id file upload-options]}}))

(re-frame/reg-event-fx
  ::upload
  upload-attachment)

(re-frame/reg-event-fx
  ::uploaded
  (fn [{:keys [db]} [_ file upload-id {:keys [entity-type uploaded-attachment-event] :as upload-options} result]]
   ;; NOTE: we can get here if upload fails because of validation (max size or max attachments)
   ;;       current way to distinguish between successful and unsuccessful uploads is to check if `(:result result)` is "ok" or "error"
   ;;       if it's "error", then we can dig the actual error message (what validation failed) from `(:message result)`
    (when (= (:result result) "ok")
      (track! "Attachment Uploaded" entity-type))

    (-> {:db (update-in db [:entity entity-type :attachments]
                        (fn [attachments]
                          (cond-> attachments
                            :always (update-in [:notifications :uploading] (fnil dec 0))
                            :always (update :requests dissoc upload-id)
                            (= "ok" (:result result)) (update-in [:notifications :uploaded] (fnil inc 0))
                            (= "ok" (:result result)) (update :data concat (:data result))
                            (= "error" (:result result)) (update-in [:notifications :failed] (fnil inc 0))
                            (= "error" (:result result)) (update-in [:notifications :errors] conj (:message result)))))
         :fx [[:dispatch [::events/fetch-history]]]}
        (conditional-dispatch uploaded-attachment-event file upload-id upload-options result))))

;; stage-upload
(defn stage-upload-attachment [{db :db}
                               [_ file {:keys [entity-type] :as upload-options}]]
  (let [upload-id (str (random-uuid))]
    {:db (update-in db [:entity entity-type :attachments :notifications :uploading]
                    (fnil inc 0))
     :http-xhrio
     {:method :post
      :uri (broutes/get-url "pattachments#staging_upload" {:project_id (subs/get-current-project-id db)})
      :body (doto (js/FormData.)
              (.append "file" file (.-name file))
              (.append "uuid" upload-id))
      :progress-handler (fn [e]
                          (js/console.log "stage-upload-attachment" e))
      :response-format (ajax/json-response-format {:keywords? true})
      :on-request [::track-upload entity-type upload-id]
      :on-success [::stage-uploaded file upload-id upload-options]
      :on-failure [::failed-upload entity-type upload-id file]}}))

(re-frame/reg-event-fx
  ::stage-upload
  stage-upload-attachment)

(re-frame/reg-event-fx
  ::stage-uploaded
  (fn [{:keys [db]} [_ file upload-id {:keys [entity-type uploaded-attachment-event] :as upload-options} result]]
    (let [url (:url result)
          stage-data {:id upload-id
                      :name (.-name file)
                      :size (.-size file)
                      :type (.-type file)
                      :url url}]
      (-> {:db (update-in db [:entity entity-type :attachments]
                          (fn [attachments]
                            (-> attachments
                                (update-in [:notifications :uploading] (fnil dec 0))
                                (update-in [:notifications :uploaded] (fnil inc 0))
                                (update :requests dissoc upload-id)
                                (update :stage conj stage-data))))
           :fx [[:dispatch [::set-stage-uploaded-data entity-type file upload-id url]]]}
          (conditional-dispatch uploaded-attachment-event file upload-id upload-options stage-data)))))

;; delete
(defn delete-attachment [{db :db} [_ {:keys [id src] :as attachment} {:keys [entity-type] :as delete-options}]]
  ;; TODO: copy-pasted from `practitest.frontend.events`
  ;;       i didn't want to re-fetch all the attachments, so the `on-success` is different
  ;;       We need to add another endpoint for deletion to simplify this request
  ;;       and response (it's a fairly large HTML response now)
  {:db (update db :progress conj [::delete entity-type id])
   :http-xhrio
   {:method :delete
    :uri src
    :body (doto (js/FormData.)
            (.append "attachment_link" "true")
            (.append "entity_type" (:entity_type attachment))
            (.append "entity_id" (:entity_id attachment)))
    :response-format (ajax/raw-response-format)
    :on-success [::deleted attachment delete-options]
    :on-failure [::events/api-error [::delete entity-type id] {:message "Error deleting attachment"}]}})

(re-frame/reg-event-fx
  ::delete
  delete-attachment)

(re-frame/reg-event-fx
  ::deleted
  (fn [{:keys [db]} [_
                     {:keys [id] :as attachment}
                     {:keys [entity-type deleted-attachment-event] :as delete-options}
                     result]]
    (-> {:db (update-in db [:entity entity-type :attachments :data]
                        (fn [data]
                          (remove #(= id (:id %)) data)))
         :fx [[:dispatch [::events/fetch-history]]]}
        (conditional-dispatch deleted-attachment-event attachment delete-options result))))

;; stage-delete
(re-frame/reg-event-fx
  ::stage-delete
  (fn [{:keys [db]} [_ entity-type {:keys [id]}]]
    {:db (update-in db [:entity entity-type :attachments :stage]
                    (fn [data]
                      (remove #(= id (:id %)) data)))}))

;; rename
(defn rename-attachment [_ [_ {:keys [id rename_uri] :as attachment} new-name {:keys [entity-type] :as rename-options}]]
  {:http-xhrio
   {:method :post
    :uri rename_uri
    :params (merge {:new_name new-name}
                   (select-keys attachment [:entity_type :entity_id]))
    :format (ajax/json-request-format)
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success [::renamed attachment rename-options]
    :on-failure [::events/api-error [::rename entity-type id] {}]}})

(re-frame/reg-event-fx
  ::rename
  rename-attachment)

(re-frame/reg-event-fx
  ::renamed
  (fn [{:keys [db]} [_ {:keys [id] :as attachment} {:keys [entity-type renamed-attachment-event] :as rename-options} result]]
    (-> {:db (update-in db [:entity entity-type :attachments :data]
                        (fn [data]
                          (mapv #(if (= id (:id %)) (:data result) %) data)))
         :fx [[:dispatch [::events/fetch-history]]]}
        (conditional-dispatch renamed-attachment-event attachment rename-options result))))

;; stage-rename
(re-frame/reg-event-fx
  ::stage-rename
  (fn [{:keys [db]} [_ entity-type id new-name]]
    {:db (update-in db [:entity entity-type :attachments :stage]
                    (fn [data]
                      (mapv #(if (= id (:id %)) (assoc % :name new-name) %) data)))}))

;; notifications
(re-frame/reg-event-db
  ::clear-notifications
  (fn [db [_ entity-type]]
    (assoc-in db [:entity entity-type :attachments :notifications] {})))
;;

;;; utils

(defn image? [mime-type]
  (when mime-type
    (string/starts-with? mime-type "image/")))

(def spreadsheet-mime-types
  #{"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    "application/vnd.ms-excel"})

(def document-mime-types
  #{"application/msword"
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"})

(def pdf-mine-types
  #{"application/pdf"})

(defn attachment-type [{:keys [type]}]
  (cond
    (image? type) :image
    (spreadsheet-mime-types type) :spreadsheet
    (document-mime-types type) :document
    (pdf-mine-types type) :pdf
    :else :generic))

;;; components

(defn menu-item [{:keys [id label on-click icon]}]
  [:a.menu-item-element
   {:id id :on-click on-click}
   [:span.icon-holder [icon]]
   [overflow/overflow-with-mux-tooltip {}
    (fn [props]
      [:div.menu-item-text props label])]])

(defn paste-listener [on-paste]
  (reagent/create-class
    {:component-did-mount (fn [_]
                            (.addEventListener (.querySelector js/document "body")
                                               "paste"
                                               on-paste))
     :component-will-unmount (fn [_]
                               (.removeEventListener (.querySelector js/document "body")
                                                     "paste"
                                                     on-paste))
     :reagent-render (fn [_]
                       [:<>])}))

(defn check-file-and-upload [{:keys [file attachments max-attachments max-attachment-size on-upload on-error]}]
  (cond
    (and (number? max-attachments) (= max-attachments (count attachments)))
    (on-error "too many attachments")

    (and (number? max-attachment-size) (< (* max-attachment-size 1024 1024) (.-size file)))
    (on-error "file is too big")

    :else
    (on-upload file)))

(defn drag-and-drop-area [{:keys [actions options attachments entity-type install-paste-listener?] :as initial-props}]
  (let [state (reagent/atom {})
        file-input-ref (atom nil)
        on-drag-over (fn [e]
                       ;; Change mouse pointer
                       (set! (.-dropEffect (.-dataTransfer e)) "copy")
                       ;; Required for on-drop to fire
                       (dom/prevent-default e)
                       (when-not (:dragged-over? @state)
                         (swap! state assoc :dragged-over? true)))
        on-drag-leave (fn [_]
                        (swap! state assoc :dragged-over? false))
        process-files (fn [source]
                        (let [{:keys [max-attachments max-attachment-size]} @options
                              ;; XXX: careful, actions is closed over initial props, should be ok in this case
                              on-upload (:on-upload actions)]
                          (doseq [file (seq (.-files source))]
                            (check-file-and-upload {:file file
                                                    :attachments @attachments
                                                    :max-attachments max-attachments
                                                    :max-attachment-size max-attachment-size
                                                    :on-upload on-upload
                                                    :on-error (fn [message] (re-frame/dispatch [::failed-upload entity-type nil file message]))}))))
        on-drop (fn [e]
                  (swap! state assoc :dragged-over? false)

                  ;; Prevent default handling
                  (dom/prevent-default e)
                  (dom/stop-propagation e)
                  (track! "Drag & Drop attachment" entity-type)
                  (process-files (.-dataTransfer e)))
        on-paste (fn [e]
                   (let [focused-element (.-activeElement js/document)]
                     (when-not (some #(= (.-tagName focused-element) %) ["INPUT" "SELECT" "TEXTAREA"])
                       (track! "Paste attachment" entity-type)
                       (dom/prevent-default e)
                       (dom/stop-propagation e)
                       (process-files (.-clipboardData e)))))
        on-select-files (fn [e]
                          (track! "Browse attachment" entity-type)
                          (process-files (.-target e)))]
    (fn [_]
      [:div.attachment-drop-zone
       {:on-drag-over on-drag-over
        :on-drag-leave on-drag-leave
        :on-drop on-drop
        :class (when (:dragged-over? @state)
                 "active")}
       (when install-paste-listener?
         [paste-listener on-paste])
       [:div.attachment-drop-zone-title
        [:input.visually-hidden
         {:ref #(reset! file-input-ref %)
          :multiple true
          :on-change on-select-files
          :type "file"}]
        [:div
         [:span.icon-wrapper [icons/mux-download]]
         (get-translation "attachments.drop-zone.drag-n-drop" "Drag & Drop")
         " / "
         [:span.ajax_attachments {:on-click (fn [e]
                                              (when-let [node @file-input-ref]
                                                (.click node))
                                              (dom/prevent-default e))}
          (get-translation "attachments.drop-zone.browse" "Browse")]
         (when install-paste-listener?
           [:<>
            " / "
            (get-translation "attachments.drop-zone.paste" "paste")])]]])))

(defn confirm-delete [{:keys [show? hide! on-delete attachment]}]
  [confirmation/confirm
   {:show? show?
    :title (get-translation "attachments.delete-confirmation.title" "Delete Attachment?")
    :body (format-translation
            (get-translation "attachments.delete-confirmation.body"
                             "\"%{attachment-name}\" will be deleted permanently.")
            :attachment-name (fu/truncate-text (:name @attachment) 30))
    :yes-label (get-translation "generic.button.delete" "Delete")
    :on-yes (fn [_]
              (hide!)
              (on-delete @attachment))
    :no-label (get-translation "generic.button.cancel" "Cancel")
    :on-no hide!}])

(defn confirm-cancel-uploads [{:keys [show? hide! on-cancel-uploads]}]
  [confirmation/confirm
   {:show? show?
    :title (get-translation "attachments.confirm-cancel-uploads.title" "Cancel the upload?")
    :body (get-translation "attachments.confirm-cancel-uploads.body" "The upload is imcomplete")
    :yes-label (get-translation "attachments.confirm-cancel-uploads.yes" "Cancel upload")
    :on-yes (fn [_]
              (hide!)
              (on-cancel-uploads))
    :no-label (get-translation "generic.button.dismiss" "Dismiss")
    :on-no hide!}])

(defn generate-attachment-url
  "This function generates a URL for the attachment based on its type.

  Parameters:
  attachment - A map representing the attachment. It should contain keys for :url and :type.

  The function generates a markdown link for the attachment.
  If the MIME type indicates that the attachment is an image, the link will be in the format ![name](url?style=thumb).
  For other types of attachments, the link will be in the format [name](url)"
  [{:keys [url name type]}]
  (if (image? type)
    (str "![" name "](" (set-query-param url "style" "thumb") ")")
    (str "[" name "](" url ")")))

(defn copy-attachment-link
  "This function copies the markdown link of an attachment to the clipboard.

  Parameters:
  attachment - A map representing the attachment. It should contain keys for :url, :name, and :type. 
               The :type key should be a MIME type that indicates the type of the attachment.

  This function generate attachment link using generate-attachment-url and copy it to the clipboard."

  [attachment]
  (dom/text->clipboard (generate-attachment-url attachment)))

(defn dropdown-menu [state _ _ _ entity-type]
  (let [hide!   #(swap! state assoc :show-menu? false)
        toggle! #(swap! state update :show-menu? not)
        confirm-delete! #(swap! state assoc :show-delete-confirmation? true)
        show-confirm-delete? (reagent/cursor state [:show-delete-confirmation?])
        hide-confirm-delete! #(swap! state assoc :show-delete-confirmation? false)]
    (fn [_ permissions {:keys [id] :as attachment} {:keys [on-preview on-delete]}]
      (let [show? (:show-menu? @state)]
        [:<>
         [overlay2/floating-ui-overlay
          {:placement        "bottom-start"
           :container        (.-body js/document)
           :offset           5
           :on-request-close hide!
           :show?            show?}

          [:div.attachment-dropdown-trigger
           {:id       (str "attachment-dropdown-" id)
            :on-click toggle!}
           [icons/mux-three-dots]]

          [:div.menu-popup
           (when (:can-preview? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-preview")
               :label    (get-translation "attachments.dropdown-menu.preview" "Preview")
               :on-click on-preview
               :icon     icons/mux-eye}])
           (when (:can-download? permissions)
             [:a.menu-item-element
              {:id (str "attachment-dropdown-" id "-download")
               :on-click (fn []
                           (track! "Download attachment" entity-type)
                           (hide!))
               :href (:url attachment)
               :download (:name attachment)}
              [:span.icon-holder [icons/mux-download2]]
              [overflow/overflow-with-mux-tooltip {}
               (fn [props]
                 [:div.menu-item-text props
                  (get-translation "generic.button.download" "Download")])]])
           (when (:can-rename? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-rename")
               :label    (get-translation "generic.button.rename" "Rename")
               :on-click (fn [_]
                           (swap! state assoc :editing-name? true)
                           (hide!))
               :icon     icons/mux-pencil-rename}])
           (when (:can-delete? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-delete")
               :label    (get-translation "generic.button.delete" "Delete")
               :on-click (fn [_]
                           (hide!)
                           (track! "Delete attachment" entity-type)
                           (confirm-delete!))
               :icon     icons/mux-trash}])
           (when (:can-copy-link? permissions)
             [menu-item
              {:id       (str "attachment-dropdown-" id "-copy-link")
               :label    (get-translation "attachments.dropdown-menu.copy-link" "Copy Link for Description")
               :on-click (fn [_]
                           (copy-attachment-link attachment)
                           (track! "Copy attachment link" entity-type)
                           (hide!))
               :icon     icons/mux-copy}])]]
         [confirm-delete
          {:show? show-confirm-delete?
           :hide! hide-confirm-delete!
           :on-delete on-delete
           :attachment (reagent/track (constantly attachment))}]]))))

(defn editable-name [{:keys [can-edit? edit! editing? value set-value! save! cancel!]}]
  (let [validating-save! (fn [event]
                           (let [target (.-target event)]
                             (when (.checkValidity target)
                               (save!))))]
    (if @editing?
      [:input.editable-input-text
       {:type "text"
        :pattern ".{1,}"
        :required true
        :auto-focus true
        :value @value
        :on-change set-value!
        :on-blur validating-save!
        :on-key-down (fn [e]
                       (case (.-key e)
                         "Escape" (do
                                    (dom/stop-propagation e)
                                    (dom/prevent-default e)
                                    (cancel!))
                         "Enter" (do
                                   (dom/stop-propagation e)
                                   (dom/prevent-default e)
                                   (validating-save! e))
                         true))}]
      [:div.editable-text
       {:on-click #(when can-edit?
                     (edit!))
        :title @value}
       @value])))

(defmulti thumbnail attachment-type)

(defmethod thumbnail :image [{:keys [url]}]
  [:div.attachment-thumbnail.image
   [:img {:src url}]])

(defmethod thumbnail :spreadsheet [_]
  [:div.attachment-thumbnail.spreadsheet
   [:span.icon-wrapper
    [icons/spreadsheet]]])

(defmethod thumbnail :document [_]
  [:div.attachment-thumbnail.document
   [:span.icon-wrapper
    [icons/document]]])

(defmethod thumbnail :pdf [_]
  [:div.attachment-thumbnail.pdf
   [icons/pdf-text]])

(defmethod thumbnail :default [_]
  [:div.attachment-thumbnail
   [icons/generic-attachment]])

(defn author-info [{:keys [author created-at]}]
  [:div.attachment-author-info
   [tooltip/mux-tooltip
    {}
    ;; Uploaded By Kenny Lane at 24/06/22, 09:21 AM
    [:<>
     [:div (format-translation
             (get-translation "attachments.info.uploaded-by"
                              "Uploaded By %{user-name}")
             :user-name (or (:name author) "unknown"))]
     [:div (format-translation
             (get-translation "attachments.info.uploaded-at"
                              "at %{date-time}")
             :date-time (fu/formatted-date-time created-at))]]
    (fn [props]
      [:span props [icons/clock]])]])

(defn size-info [{:keys [size]}]
  [:div.attachment-size
   [:span.size-title (str (get-translation "attachments.info.size" "Size:") " ")]
   (fu/format-file-size size)])

(defn card [permissions _ {:keys [on-rename on-preview] :as actions} entity-type]
  (let [state (reagent/atom {:editing-name? false})]
    (fn [_ attachment _]
      (let [name-container (reagent/atom {:name (:name attachment)})]
        [:div.attachment-card.attachment-item
         [:div
          {:on-click #(on-preview)}
          [thumbnail attachment]]
         [:div.attachment-meta
          [:div.attachment-name
           [editable-name
            {:can-edit? (:can-rename? permissions)
             :editing? (reagent/cursor state [:editing-name?])
             :edit! #(swap! state assoc :editing-name? true)
             :value (reagent/cursor name-container [:name])
             :set-value! #(swap! name-container assoc :name (dom/event-value %))
             :save! (fn []
                      (when (not= (:name attachment) (:name @name-container))
                        (track! "Rename attachment" entity-type)
                        (on-rename attachment (:name @name-container)))
                      (swap! state assoc :editing-name? false))
             :cancel! (fn []
                        (swap! name-container assoc :name (:name attachment))
                        (swap! state assoc :editing-name? false))}]]
          [:div.attachment-info
           [size-info attachment]
           [author-info attachment]]]
         [dropdown-menu state permissions attachment actions entity-type]]))))

(defn notification-area [notifications {:keys [on-clear-notifications on-cancel-uploads]}]
  (let [{:keys [uploading uploaded failed]} @notifications]
    (when (or (pos? uploading) (pos? uploaded) (pos? failed))
      (let [icon (cond
                   (pos? uploading) [icons/mux-spinner]
                   (pos? failed) [:div.text-error [icons/mux-notification-error]]
                   :else [:div.text-success [icons/mux-notification-success]])
            ;; NOTE: not sure it's a good idea to join and capitalize localized strings
            status (->> [(when (pos? uploading)
                           (format-translation
                             (get-translation "attachments.notifications.uploading"
                                              "uploading %{n} files")
                             :n uploading))
                         (when (pos? uploaded)
                           (format-translation
                             (get-translation "attachments.notifications.uploaded"
                                              "uploaded %{n} files")
                             :n uploaded))
                         (when (pos? failed)
                           (format-translation
                             (get-translation "attachments.notifications.failed"
                                              "%{n} failed")
                             :n failed))]
                        (remove nil?)
                        (string/join ", ")
                        (string/capitalize))
            button (if (pos? uploading)
                     [:a.attachments-notifications-discard
                      {:href "#"
                       :on-click (fn [e]
                                   (dom/stop-propagation e)
                                   (dom/prevent-default e)
                                   (on-cancel-uploads))}
                      (get-translation "generic.button.cancel" "Cancel")]
                     [:a.attachments-notifications-discard
                      {:href "#"
                       :on-click (fn [e]
                                   (dom/stop-propagation e)
                                   (dom/prevent-default e)
                                   (on-clear-notifications))}
                      (get-translation "generic.button.close"
                                       "Close")])]

        [:div.attachments-notifications-container
         [:div.attachments-notifications
          [:div.attachments-notifications-icon icon]
          [:div.attachments-notifications-status status]
          button]]))))

(defn preview-modal-header [{:keys [previous! next! current-title]}]
  [:div.attachments-preview-title
   [:span (get-translation "attachments.preview.title" "Preview Attachment")]
   [:span.navigation-button {:on-click previous!} [icons/mux-chevron-left]]
   [:span @current-title]
   [:span.navigation-button {:on-click next!} [icons/mux-chevron-right]]])

(defn preview-editable-name [{:keys [current-attachment on-rename] :as actions}]
  (let [state (reagent/atom {:editing? false
                             :name (:name @current-attachment)})]
    [editable-name
     (assoc actions
            :can-edit? (boolean on-rename)
            :edit! #(swap! state assoc :editing? true)
            :save! (fn []
                     (when (not= (:name @current-attachment) (:name @state))
                       (on-rename @current-attachment (:name @state)))
                     (swap! state assoc :editing? false))
            :cancel! (fn []
                       (swap! state assoc
                              :name (:name @current-attachment)
                              :editing? false))
            :editing? (reagent/cursor state [:editing?])
            :value (reagent/cursor state [:name])
            :set-value! #(swap! state assoc :name (dom/event-value %)))]))

(defn not-previewable-attachment [{:keys [url name]}]
  [:<>
   [:p (get-translation "attachments.preview.cannot-preview" "The file cannot be previewed.")]
   [:p {:dangerouslySetInnerHTML
        {:__html (format-translation
                   (get-translation "attachments.preview.can-download" "You can %{download_text_link} it")
                   :download_text_link (str "<a href=" url " download=" name ">"
                                            (get-translation "generic.button.download" "download")
                                            "</a>"))}}]])

(defmulti preview-content attachment-type)

(defmethod preview-content :image [{:keys [url]}]
  [:div.attachments-preview-content
   [:img {:src url}]])

(defmethod preview-content :spreadsheet [attachment]
  [:div.attachments-preview-content.spreadsheet
   [icons/mux-spreadsheet]
   [not-previewable-attachment attachment]])

(defmethod preview-content :document [attachment]
  [:div.attachments-preview-content.document
   [icons/mux-document]
   [not-previewable-attachment attachment]])

(defmethod preview-content :pdf [{:keys [file_url name] :as attachment}]
  [:div.attachments-preview-content.pdf
   [:iframe {:src file_url
             :title name}]])

(defmethod preview-content :default [attachment]
  [:div.attachments-preview-content
   [icons/mux-generic-attachment]
   [not-previewable-attachment attachment]])

(defn preview-modal-body [{:keys [show-delete-confirmation!
                                  current-attachment previous! next!] :as actions}]
  [:div.attachments-preview-body
   [:div.attachments-preview-toolbar
    [preview-editable-name actions]
    [:div.attachments-preview-toolbar-buttons.m-button-group
     (when show-delete-confirmation!
       [buttons/button
        {:on-click show-delete-confirmation!
         :icon [icons/mux-trash]
         :text (get-translation "generic.button.delete" "Delete")}])
     [buttons/button
      {:href (:url @current-attachment)
       :download (:name @current-attachment)
       :icon [icons/mux-download2]
       :text (get-translation "generic.button.download" "Download")}]]]
   [preview-content @current-attachment]
   [:span.attachments-preview-navigation.attachments-preview-previous
    {:on-click previous!}
    [icons/mux-chevron-left]]
   [:span.attachments-preview-navigation.attachments-preview-next
    {:on-click next!}
    [icons/mux-chevron-right]]])

(defn preview-modal [{:keys [show? hide!
                             show-delete-confirmation? hide-delete-confirmation!
                             on-delete current-attachment] :as actions}]
  [:<>
   [modal/bootstrap-modal
    {:class "attachments-preview-container"
     :is-open? @show?
     :on-request-close hide!
     :close-button? true
     :centered? true}
    [preview-modal-header actions]
    [preview-modal-body actions]
    nil]
   (when on-delete
     [confirm-delete
      {:show? show-delete-confirmation?
       :hide! hide-delete-confirmation!
       :on-delete on-delete
       :attachment current-attachment}])])

(defn cards [{:keys [state permissions actions attachments entity-type]}]
  [:div.attachment-cards
   (for [[i {:keys [id] :as attachment}] attachments]
     ^{:key (str "att-" id)}
     [card permissions attachment
      (merge actions
             {:on-preview (fn []
                            (track! "Preview attachment" entity-type)
                            (swap! state assoc
                                   :current-attachment-idx i
                                   :show-preview? true))})
      entity-type])])

(defn notifications-view [{:keys [state notifications actions]}]
  (let [show-cancel-uploads? (reagent/cursor state [:show-cancel-uploads?])
        show-cancel-uploads! #(swap! state assoc :show-cancel-uploads? true)
        hide-cancel-uploads! #(swap! state assoc :show-cancel-uploads? false)
        on-cancel-uploads (:on-cancel-uploads actions)]
    [:<>
     [confirm-cancel-uploads
      {:show? show-cancel-uploads?
       :hide! hide-cancel-uploads!
       :on-cancel-uploads on-cancel-uploads}]
     [notification-area notifications
      (assoc actions :on-cancel-uploads show-cancel-uploads!)]]))

(defn preview [{:keys [state indexed-attachments actions]}]
  (let [hide-preview! #(swap! state assoc :show-preview? false)
        previous-preview! (fn []
                            (swap! state update :current-attachment-idx
                                   (fn [current-attachment-idx]
                                     (if (zero? current-attachment-idx)
                                       (dec (count indexed-attachments))
                                       (dec current-attachment-idx)))))
        next-preview! (fn []
                        (swap! state update :current-attachment-idx
                               (fn [current-attachment-idx]
                                 (if (= (dec (count indexed-attachments))
                                        current-attachment-idx)
                                   0
                                   (inc current-attachment-idx)))))
        current-attachment (reagent/track
                             (fn []
                               (when-let [idx (:current-attachment-idx @state)]
                                 (last (first (drop idx indexed-attachments))))))
        current-title (reagent/track
                        (fn []
                          (str (inc (:current-attachment-idx @state))
                               "/"
                               (count indexed-attachments))))
        show-delete-confirmation? (reagent/cursor state [:show-preview-delete-confirmation?])
        show-delete-confirmation! #(swap! state assoc :show-preview-delete-confirmation? true)
        hide-delete-confirmation! #(swap! state assoc :show-preview-delete-confirmation? false)]
    [preview-modal
     (-> actions
         (assoc :show? (reagent/cursor state [:show-preview?])
                :hide! hide-preview!
                :previous! previous-preview!
                :next! next-preview!
                :current-title  current-title
                :current-attachment current-attachment
                :show-delete-confirmation? show-delete-confirmation?
                :show-delete-confirmation! (when (:on-delete actions) show-delete-confirmation!)
                :hide-delete-confirmation! hide-delete-confirmation!)
         (update :on-delete
                 (fn [on-delete]
                   (when on-delete
                     (fn []
                       (hide-preview!)
                       (on-delete @current-attachment))))))]))

(defn attachments-view []
  (let [state (reagent/atom {:search-string ""
                             :show-cancel-uploads? false})]
    (fn [{:keys [options attachments actions notifications entity-type show-search? install-paste-listener?] :as props}]
      (let [indexed-attachments (vec (map-indexed vector @attachments))
            show-search? (if (contains? props :show-search?)
                           show-search?
                           true)
            install-paste-listener? (if (contains? props :install-paste-listener?)
                                      install-paste-listener?
                                      true)
            {:keys [permissions]} @options
            filtered-attachments (if (string/blank? (:search-string @state))
                                   indexed-attachments
                                   (vec (filter (fn [[_ {:keys [name]}]]
                                                  (fu/substring-search? name (:search-string @state)))
                                                indexed-attachments)))]
        [:div.form-attachments-container
         [:div.dropzone-search-wrapper
          (when (:can-upload? permissions)
            [drag-and-drop-area {:actions actions
                                 :options options
                                 :attachments attachments
                                 :entity-type entity-type
                                 :install-paste-listener? install-paste-listener?}])
          (when show-search?
            [search-box/search-textbox
             {:placeholder (get-translation "entity.attachment.search-placeholder" "Search Attachment")
              :value (:search-string @state)
              :on-change #(swap! state assoc :search-string (dom/event-value %))
              :on-clear-click #(swap! state assoc :search-string "")
              :on-focus #(track! "Search Attachment" entity-type)}])]
         [cards {:state state
                 :permissions permissions
                 :actions actions
                 :attachments filtered-attachments}]
         [notifications-view {:state state
                              :notifications notifications
                              :actions actions}]
         [preview {:state state
                   :indexed-attachments indexed-attachments
                   :actions (cond-> actions
                              (not (:can-rename? permissions))
                              (dissoc :on-rename)

                              (not (:can-delete? permissions))
                              (dissoc :on-delete))}]]))))
